
import React, { useRef, useState } from "react";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Image, Video, Upload, X } from "lucide-react";
import { toast } from "@/hooks/use-toast";

interface MediaUploadProps {
  onChange: (files: FileList) => void;
  error?: string;
  selectedFiles?: File[];
}

const MediaUpload: React.FC<MediaUploadProps> = ({ onChange, error, selectedFiles = [] }) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrls, setPreviewUrls] = useState<string[]>([]);

  // Generate preview URLs when selectedFiles changes
  React.useEffect(() => {
    // Revoke previous object URLs to prevent memory leaks
    previewUrls.forEach(url => URL.revokeObjectURL(url));
    
    const newUrls = selectedFiles.map(file => URL.createObjectURL(file));
    setPreviewUrls(newUrls);
    
    // Cleanup when component unmounts
    return () => {
      newUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [selectedFiles]);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      // Check file sizes before passing to parent
      const files = Array.from(e.target.files);
      const oversizedFiles = files.filter(file => file.size > 5 * 1024 * 1024);
      
      if (oversizedFiles.length > 0) {
        toast({
          title: "File size exceeded",
          description: `${oversizedFiles.length} files exceed the 5MB limit and won't be uploaded.`,
          variant: "destructive",
        });
        
        // Filter out oversized files
        const validFiles = files.filter(file => file.size <= 5 * 1024 * 1024);
        
        // Create a new FileList-like object
        const dataTransfer = new DataTransfer();
        validFiles.forEach(file => dataTransfer.items.add(file));
        
        // Pass only valid files to parent
        if (dataTransfer.files.length > 0) {
          onChange(dataTransfer.files);
          console.log("Valid files selected:", dataTransfer.files);
        }
      } else {
        // All files are valid
        onChange(e.target.files);
        console.log("Files selected:", e.target.files);
      }
    }
  };

  const renderPreview = (file: File, index: number) => {
    const isImage = file.type.startsWith('image/');
    const isVideo = file.type.startsWith('video/');
    const previewUrl = previewUrls[index];
    
    return (
      <div key={`${file.name}-${index}`} className="relative">
        <div className="border rounded-md overflow-hidden h-24 w-24 flex items-center justify-center bg-gray-50">
          {isImage && previewUrl && (
            <img src={previewUrl} alt={file.name} className="h-full w-full object-cover" />
          )}
          {isVideo && (
            <div className="flex flex-col items-center justify-center">
              <Video className="h-10 w-10 text-gray-400" />
              <span className="text-xs text-gray-500 mt-1">{file.name.slice(0, 10)}...</span>
            </div>
          )}
          {!isImage && !isVideo && (
            <div className="flex flex-col items-center justify-center">
              <span className="text-xs text-gray-500">{file.name.slice(0, 10)}...</span>
            </div>
          )}
        </div>
        <div className="absolute top-1 right-1 bg-white rounded-full shadow p-1">
          <span className="text-xs text-gray-600">{(file.size / (1024 * 1024)).toFixed(1)}MB</span>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-2">
      <Label htmlFor="media-upload">Business Photos & Videos (Optional)</Label>
      <div className="flex flex-col gap-2">
        <Button
          type="button"
          variant="outline"
          onClick={handleButtonClick}
          className="w-full flex items-center justify-center gap-2 h-20 border-dashed"
        >
          <Upload className="h-5 w-5" />
          <span>Upload Images or Videos</span>
        </Button>
        
        {selectedFiles.length > 0 && (
          <div className="mt-4">
            <h4 className="text-sm font-medium mb-2">Selected Files ({selectedFiles.length})</h4>
            <div className="flex flex-wrap gap-2">
              {selectedFiles.map((file, index) => renderPreview(file, index))}
            </div>
          </div>
        )}
        
        <div className="text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <Image className="h-3 w-3" /> Accepted image formats: JPG, PNG, GIF (max 5MB)
          </div>
          <div className="flex items-center gap-1">
            <Video className="h-3 w-3" /> Accepted video formats: MP4, MOV, AVI (max 5MB)
          </div>
        </div>
        <input
          ref={fileInputRef}
          id="media-upload"
          type="file"
          accept="image/jpeg,image/png,image/gif,video/mp4,video/quicktime,video/x-msvideo"
          multiple
          className="hidden"
          onChange={handleFileChange}
        />
        {error && <p className="text-sm text-red-500">{error}</p>}
      </div>
    </div>
  );
};

export default MediaUpload;
